import { Component, ComponentType, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Stack,
  Alert,
} from '@mui/joy';
import {
  ErrorOutline,
  Refresh,
  Home,
  BugReport,
} from '@mui/icons-material';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // You can also log the error to an error reporting service here
    // logErrorToService(error, errorInfo);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReportBug = () => {
    // You can implement bug reporting functionality here
    const errorDetails = `
Error: ${this.state.error?.message}
Stack: ${this.state.error?.stack}
Component Stack: ${this.state.errorInfo?.componentStack}
    `;

    // For now, just copy to clipboard
    navigator.clipboard.writeText(errorDetails).then(() => {
      alert(
        this.props.t(
          'Error details copied to clipboard. Please share this with the support team.'
        )
      );
    });
  };

  override render() {
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.surface',
            p: 2,
          }}
        >
          <Card
            variant="outlined"
            sx={{
              maxWidth: 600,
              width: '100%',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Stack spacing={3} alignItems="center">
              {/* Error Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'danger.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ErrorOutline
                  sx={{
                    fontSize: 40,
                    color: 'danger.500',
                  }}
                />
              </Box>

              {/* Error Title */}
              <Typography level="h2" color="danger">
                {t('Oops! Something went wrong')}
              </Typography>

              {/* Error Description */}
              <Typography level="body-lg" color="neutral" textAlign="center">
                {t(
                  "We're sorry, but an unexpected error has occurred. This might be a temporary issue."
                )}
              </Typography>

              {/* Error Details (only in development) */}
              {import.meta.env.DEV && this.state.error && (
                <Alert
                  variant="soft"
                  color="warning"
                  sx={{ width: '100%', textAlign: 'left' }}
                  startDecorator={<BugReport />}
                >
                  <Box>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      {t('Error Details (Development Mode):')}
                    </Typography>
                    <Typography level="body-sm" sx={{ fontFamily: 'var(--joy-fontFamily-body)', fontVariantNumeric: 'tabular-nums' }}>
                      {this.state.error.message}
                    </Typography>
                  </Box>
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<Refresh />}
                  onClick={this.handleRefresh}
                >
                  {t('Refresh Page')}
                </Button>

                <Button
                  variant="outlined"
                  color="neutral"
                  startDecorator={<Home />}
                  onClick={this.handleGoHome}
                >
                  {t('Go Home')}
                </Button>

                <Button
                  variant="soft"
                  color="warning"
                  startDecorator={<BugReport />}
                  onClick={this.handleReportBug}
                >
                  {t('Report Issue')}
                </Button>
              </Stack>

              {/* Help Text */}
              <Typography level="body-sm" color="neutral" sx={{ mt: 2 }}>
                {t(
                  'If this problem persists, please contact our support team.'
                )}
              </Typography>
            </Stack>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

const TranslatedErrorBoundary: ComponentType<
  Omit<Props, keyof WithTranslation>
> = withTranslation()(ErrorBoundary);

export default TranslatedErrorBoundary;
